import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CategorySelectorComponent } from '../../shared/design-system/category-selector.component';
import { MarketplaceStoreSelectorComponent } from '../../shared/design-system/marketplace-store-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { StorefrontPlacementKeySelectorComponent } from '../../shared/design-system/storefront-placement-key-selector.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import {
  CatalogOffering,
  CatalogService,
  CategoryResponse,
  StorefrontContentBlock,
  StorefrontSection,
  StorefrontSectionItem
} from './catalog.service';
import { STOREFRONT_SECTION_RULE_KEYS } from './marketplace-storefront-selectors.model';
import {
  MARKETPLACE_LIST_TOOLBAR_STYLES,
  MarketplaceLiveSubscription,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeMarketplaceOperationsLiveUpdates,
  toggleSortField,
  unsubscribeMarketplaceOperationsLiveUpdates
} from './marketplace-list-page.utils';

type SectionSortField = 'placementKey' | 'title' | 'mode' | 'status' | 'displayOrder';

@Component({
  standalone: true,
  selector: 'ain-storefront-home-composer',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    StructuredJsonFormComponent,
    MarketplaceStoreSelectorComponent,
    StorefrontPlacementKeySelectorComponent,
    CategorySelectorComponent,
    OfferingSelectorComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TextareaModule
  ],
  template: `
    <ain-page
      [title]="t('catalog.storefront.homeComposer.title')"
      [description]="t('catalog.storefront.homeComposer.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'catalog.marketplace.help.list.title'"
        [descriptionKey]="'catalog.marketplace.help.list.description'"
        [bulletKeys]="[
          'catalog.marketplace.help.list.bullets.selection',
          'catalog.marketplace.help.list.bullets.filters',
          'catalog.marketplace.help.list.bullets.live'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <ain-marketplace-store-selector
              [(ngModel)]="selectedStoreId"
              (ngModelChange)="onStoreChange()" />
            <button pButton type="button" [label]="t('catalog.storefront.homeComposer.addSection')" (click)="openSectionDialog()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.marketplace.actions.export')" (click)="exportRows()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('catalog.marketplace.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'catalog.marketplace.help.filters.title'"
                [descriptionKey]="'catalog.marketplace.help.filters.description'"
                [bulletKeys]="['catalog.marketplace.help.filters.bullets.search']" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('catalog.marketplace.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
            </div>
          </details>

          @if (!selectedStoreId) {
            <ain-state type="empty" [title]="t('catalog.storefront.selectStore')" />
          } @else if (filteredSections().length === 0) {
            <ain-state type="empty" [title]="t('catalog.storefront.empty')" />
          } @else {
            <p-table
              [value]="filteredSections()"
              dataKey="id"
              [paginator]="true"
              [rows]="15"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'placementKey'" (click)="sortBy('placementKey')">{{ t('catalog.storefront.homeComposer.fields.placementKey') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'title'" (click)="sortBy('title')">{{ t('columns.title') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'mode'" (click)="sortBy('mode')">{{ t('catalog.storefront.homeComposer.fields.mode') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'status'" (click)="sortBy('status')">{{ t('columns.status') }}</button></th>
                  <th><button type="button" class="sort-btn" [class.active]="sortField() === 'displayOrder'" (click)="sortBy('displayOrder')">{{ t('catalog.storefront.publishing.order') }}</button></th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td>{{ row.placementKey }}</td>
                  <td>{{ row.title }}</td>
                  <td>{{ row.mode }}</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ row.displayOrder }}</td>
                  <td class="actions">
                    <button pButton type="button" size="small" [label]="t('catalog.storefront.homeComposer.items')" (click)="openItems(row)"></button>
                    <button pButton type="button" size="small" [label]="t('catalog.storefront.publishing.publish')" (click)="openPublishDialog(row.id)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
        </section>
      }

      <p-dialog [header]="t('catalog.storefront.homeComposer.addSection')" [(visible)]="sectionDialogVisible" [modal]="true" [style]="{ width: '520px' }">
        <div class="form">
          <label>
            <span>{{ t('catalog.marketplace.filters.store') }}</span>
            <ain-marketplace-store-selector [(ngModel)]="sectionDialogStoreId" class="full" />
          </label>
          <label>{{ t('catalog.storefront.homeComposer.fields.placementKey') }}</label>
          <ain-storefront-placement-key-selector [scope]="'section'" [(ngModel)]="placementKey" class="full" />
          <label>{{ t('columns.title') }}</label>
          <input pInputText [(ngModel)]="sectionTitle" class="full" />
          <label>{{ t('catalog.storefront.homeComposer.fields.mode') }}</label>
          <p-select
            [options]="modeOptions"
            optionValue="value"
            [(ngModel)]="sectionMode"
            appendTo="body"
            class="full">
            <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
            <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
          </p-select>
          <label>{{ t('catalog.storefront.homeComposer.fields.ruleKey') }}</label>
          <p-select
            [options]="sectionRuleKeyOptions"
            optionValue="value"
            [(ngModel)]="sectionRuleKey"
            [showClear]="true"
            [placeholder]="t('catalog.selectors.ruleKey.placeholder')"
            appendTo="body"
            class="full">
            <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
            <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
          </p-select>
          <ain-structured-json-form [fields]="sectionRuleConfigFields()" [(payloadJson)]="sectionRuleConfigJson" />
          <label>{{ t('catalog.storefront.publishing.order') }}</label>
          <input pInputText type="number" [(ngModel)]="sectionOrder" class="full" />
          @if (sectionDialogError()) { <p class="error">{{ sectionDialogError() }}</p> }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeSectionDialog()"></button>
          <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="acting()" (click)="saveSection()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog [header]="t('catalog.storefront.homeComposer.items')" [(visible)]="itemsDialogVisible" [modal]="true" [style]="{ width: '640px' }">
        <div class="form">
          @if (selectedSectionPlacementKey() === 'home.hero') {
            <ain-info-hint
              [titleKey]="'catalog.storefront.homeComposer.heroItemsHint.title'"
              [descriptionKey]="'catalog.storefront.homeComposer.heroItemsHint.description'"
              [bulletKeys]="[
                'catalog.storefront.homeComposer.heroItemsHint.bullets.offering',
                'catalog.storefront.homeComposer.heroItemsHint.bullets.category',
                'catalog.storefront.homeComposer.heroItemsHint.bullets.contentBlock',
                'catalog.storefront.homeComposer.heroItemsHint.bullets.publish'
              ]" />
          }
          <label>{{ t('catalog.storefront.publishing.offering') }}</label>
          <ain-offering-selector [(ngModel)]="itemOfferingId" class="full" />
          <label>{{ t('catalog.storefront.categoryNavigation.fields.category') }}</label>
          <ain-category-selector [(ngModel)]="itemCategoryId" class="full" />
          <label>{{ t('catalog.storefront.homeComposer.fields.contentBlock') }}</label>
          <p-select
            [options]="publishedContentBlockOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="itemContentBlockId"
            [showClear]="true"
            [placeholder]="t('catalog.storefront.homeComposer.fields.contentBlockPlaceholder')"
            appendTo="body"
            class="full" />
          <label>{{ t('catalog.storefront.publishing.order') }}</label>
          <input pInputText type="number" [(ngModel)]="itemDisplayOrder" class="full" />
          @if (itemDialogError()) { <p class="error">{{ itemDialogError() }}</p> }
          <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="acting() || !selectedSectionId()" (click)="saveItem()"></button>
          <p-table [value]="items()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.storefront.publishing.offering') }}</th>
                <th>{{ t('catalog.storefront.categoryNavigation.fields.category') }}</th>
                <th>{{ t('catalog.storefront.homeComposer.fields.contentBlock') }}</th>
                <th>{{ t('catalog.storefront.publishing.order') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ offeringLabel(row.offeringId) }}</td>
                <td>{{ categoryLabel(row.categoryId) }}</td>
                <td>{{ contentBlockLabel(row.contentBlockId) }}</td>
                <td>{{ row.displayOrder }}</td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </p-dialog>

      <p-dialog [header]="t('catalog.marketplace.storefront.confirm.publishTitle')" [(visible)]="publishDialogVisible" [modal]="true" [style]="{ width: '440px' }">
        <label class="form">
          <span>{{ t('catalog.marketplace.storefront.confirm.reason') }}</span>
          <textarea pTextarea [(ngModel)]="publishReason" rows="3" class="full"></textarea>
        </label>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="publishDialogVisible = false"></button>
          <button pButton type="button" [label]="t('catalog.storefront.publishing.publish')" [disabled]="acting() || !publishReason.trim()" (click)="confirmPublish()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .form { display: grid; gap: 10px; }
    .error { color: #b91c1c; font-size: 13px; margin: 0; }
    ${MARKETPLACE_LIST_TOOLBAR_STYLES}
  `]
})
export class StorefrontHomeComposerComponent implements OnInit, OnDestroy {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private marketplaceLive: MarketplaceLiveSubscription = { topic: null, eventsSubscription: null };

  readonly loading = signal(false);
  readonly acting = signal(false);
  readonly info = signal('');
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly categories = signal<CategoryResponse[]>([]);
  readonly sections = signal<StorefrontSection[]>([]);
  readonly items = signal<StorefrontSectionItem[]>([]);
  readonly contentBlocks = signal<StorefrontContentBlock[]>([]);
  readonly selectedSectionId = signal<string | null>(null);
  readonly selectedSectionPlacementKey = signal<string | null>(null);
  readonly itemDialogError = signal('');
  readonly selectedRows = signal<StorefrontSection[]>([]);
  readonly search = signal('');
  readonly sortField = signal<SectionSortField>('displayOrder');
  readonly sortOrder = signal<1 | -1>(1);

  liveStatusKey = liveStatusKey;

  readonly modeOptions = [
    { value: 'Manual', labelKey: 'catalog.storefront.homeComposer.mode.manual' },
    { value: 'RuleBased', labelKey: 'catalog.storefront.homeComposer.mode.ruleBased' }
  ];
  readonly sectionRuleKeyOptions = STOREFRONT_SECTION_RULE_KEYS.map(key => ({
    value: key,
    labelKey: `catalog.selectors.ruleKey.${key}`
  }));

  selectedStoreId: string | null = null;
  sectionDialogVisible = false;
  sectionDialogStoreId: string | null = null;
  readonly sectionDialogError = signal('');
  itemsDialogVisible = false;
  publishDialogVisible = false;
  pendingPublishSectionId = '';
  publishReason = '';
  placementKey: string | null = 'home.hero';
  sectionTitle = '';
  sectionMode = 'Manual';
  sectionRuleKey: string | null = null;
  sectionRuleConfigJson = '{}';
  sectionOrder = 10;
  itemOfferingId: string | null = null;
  itemCategoryId: string | null = null;
  itemContentBlockId: string | null = null;
  itemDisplayOrder = 10;
  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.service.listOfferings().subscribe({ next: rows => this.offerings.set(rows) });
    this.service.listCategories().subscribe({ next: rows => this.categories.set(rows) });
    void this.startLiveUpdates();
  }

  sectionRuleConfigFields(): StructuredJsonFieldDef[] {
    return [
      { key: 'maxItems', type: 'number', labelKey: 'catalog.storefront.homeComposer.ruleConfig.maxItems' },
      {
        key: 'categorySlug',
        type: 'select',
        labelKey: 'catalog.storefront.homeComposer.ruleConfig.categorySlug',
        options: this.categories().map(category => ({
          value: category.slug,
          label: `${category.path} (${category.slug})`
        }))
      }
    ];
  }

  ngOnDestroy(): void {
    void unsubscribeMarketplaceOperationsLiveUpdates(this.liveUpdateService, this.marketplaceLive);
  }

  onStoreChange(): void {
    this.selectedRows.set([]);
    this.load();
  }

  load(): void {
    if (!this.selectedStoreId) {
      this.loading.set(false);
      this.sections.set([]);
      return;
    }

    this.loading.set(true);
    this.info.set('');
    this.service.listStorefrontSections(this.selectedStoreId).subscribe({
      next: rows => {
        this.sections.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.sections.set([]);
        this.loading.set(false);
      }
    });
  }

  filteredSections(): StorefrontSection[] {
    const value = this.search().trim().toLowerCase();
    const filtered = !value
      ? this.sections()
      : this.sections().filter(row =>
        row.placementKey.toLowerCase().includes(value)
        || row.title.toLowerCase().includes(value)
        || row.mode.toLowerCase().includes(value)
        || row.status.toLowerCase().includes(value));

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field]) * order);
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: StorefrontSection[] | StorefrontSection | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  sortBy(field: SectionSortField): void {
    toggleSortField(
      this.sortField(),
      field,
      this.sortOrder(),
      value => this.sortField.set(value),
      value => this.sortOrder.set(value)
    );
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.bulkNothingSelected'));
      return;
    }
    this.info.set(this.t('catalog.marketplace.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredSections();
    if (rows.length === 0) {
      this.info.set(this.t('catalog.marketplace.actions.exportEmpty'));
      return;
    }
    downloadCsv(
      'storefront-sections.csv',
      ['PlacementKey', 'Title', 'Mode', 'Status', 'DisplayOrder'],
      rows.map(row => [row.placementKey, row.title, row.mode, row.status, row.displayOrder.toString()]));
    this.info.set(this.t('catalog.marketplace.actions.exportReady'));
  }

  openSectionDialog(): void {
    this.sectionDialogStoreId = this.selectedStoreId;
    this.placementKey = 'home.hero';
    this.sectionTitle = '';
    this.sectionMode = 'Manual';
    this.sectionRuleKey = null;
    this.sectionRuleConfigJson = '{}';
    this.sectionOrder = (this.sections().length + 1) * 10;
    this.sectionDialogError.set('');
    this.sectionDialogVisible = true;
  }

  closeSectionDialog(): void {
    this.sectionDialogVisible = false;
    this.sectionDialogError.set('');
  }

  saveSection(): void {
    this.sectionDialogError.set('');
    const storeId = this.sectionDialogStoreId?.trim() || this.selectedStoreId?.trim();
    const placementKey = this.placementKey?.trim();
    const title = this.sectionTitle.trim();
    if (!storeId) {
      this.sectionDialogError.set(this.t('catalog.storefront.publishing.validation.storeRequired'));
      return;
    }
    if (!placementKey) {
      this.sectionDialogError.set(this.t('catalog.storefront.publishing.validation.placementKeyRequired'));
      return;
    }
    if (!title) {
      this.sectionDialogError.set(this.t('catalog.storefront.publishing.validation.titleRequired'));
      return;
    }

    this.acting.set(true);
    this.service.upsertStorefrontSection({
      storeId,
      placementKey,
      title,
      mode: this.sectionMode,
      ruleKey: this.sectionRuleKey?.trim() || null,
      ruleConfigurationJson: this.sectionRuleConfigJson.trim() || '{}',
      displayOrder: this.parseDisplayOrder(this.sectionOrder)
    }).subscribe({
      next: () => {
        this.selectedStoreId = storeId;
        this.closeSectionDialog();
        this.load();
        this.acting.set(false);
      },
      error: () => {
        this.sectionDialogError.set(this.t('catalog.storefront.publishing.validation.saveFailed'));
        this.acting.set(false);
      }
    });
  }

  private parseDisplayOrder(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 10;
  }

  openPublishDialog(id: string): void {
    this.pendingPublishSectionId = id;
    this.publishReason = '';
    this.publishDialogVisible = true;
  }

  confirmPublish(): void {
    if (!this.pendingPublishSectionId || !this.publishReason.trim()) {
      this.info.set(this.t('catalog.marketplace.storefront.confirm.reasonRequired'));
      return;
    }
    this.acting.set(true);
    this.service.publishStorefrontSection(this.pendingPublishSectionId).subscribe({
      next: () => {
        this.publishDialogVisible = false;
        this.pendingPublishSectionId = '';
        this.publishReason = '';
        this.load();
        this.acting.set(false);
      },
      error: () => this.acting.set(false)
    });
  }

  openItems(section: StorefrontSection): void {
    this.selectedSectionId.set(section.id);
    this.selectedSectionPlacementKey.set(section.placementKey);
    this.itemOfferingId = null;
    this.itemCategoryId = null;
    this.itemContentBlockId = null;
    this.itemDisplayOrder = (this.items().length + 1) * 10;
    this.itemDialogError.set('');
    this.itemsDialogVisible = true;
    this.service.listStorefrontSectionItems(section.id).subscribe({
      next: rows => this.items.set(rows),
      error: () => this.items.set([])
    });
    this.service.listStorefrontContentBlocks(section.storeId, section.placementKey).subscribe({
      next: rows => this.contentBlocks.set(rows),
      error: () => this.contentBlocks.set([])
    });
  }

  publishedContentBlockOptions(): Array<{ value: string; label: string }> {
    return this.contentBlocks()
      .filter(row => row.status === 'Published')
      .map(row => ({
        value: row.id,
        label: `${row.title} (${row.placementKey})`
      }));
  }

  saveItem(): void {
    const sectionId = this.selectedSectionId();
    if (!sectionId) return;

    this.itemDialogError.set('');
    const offeringId = this.itemOfferingId?.trim() || null;
    const categoryId = this.itemCategoryId?.trim() || null;
    const contentBlockId = this.itemContentBlockId?.trim() || null;
    if (!offeringId && !categoryId && !contentBlockId) {
      this.itemDialogError.set(this.t('catalog.storefront.homeComposer.validation.itemTargetRequired'));
      return;
    }

    this.acting.set(true);
    this.service.upsertStorefrontSectionItem(sectionId, {
      sectionId,
      offeringId,
      categoryId,
      sellerId: null,
      contentBlockId,
      navigationItemId: null,
      displayOrder: this.parseDisplayOrder(this.itemDisplayOrder),
      customDataJson: '{}'
    }).subscribe({
      next: () => {
        this.itemOfferingId = null;
        this.itemCategoryId = null;
        this.itemContentBlockId = null;
        this.service.listStorefrontSectionItems(sectionId).subscribe({
          next: rows => {
            this.items.set(rows);
            this.itemDisplayOrder = (rows.length + 1) * 10;
          },
          error: () => this.items.set([])
        });
        this.acting.set(false);
      },
      error: () => {
        this.itemDialogError.set(this.t('catalog.storefront.publishing.validation.saveFailed'));
        this.acting.set(false);
      }
    });
  }

  statusKey(status: string): string {
    return `catalog.marketplace.storefront.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  offeringLabel(offeringId: string | null): string {
    if (!offeringId) return '—';
    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : offeringId;
  }

  categoryLabel(categoryId: string | null): string {
    if (!categoryId) return '—';
    const category = this.categories().find(item => item.id === categoryId);
    return category ? `${category.path} (${category.slug})` : categoryId;
  }

  contentBlockLabel(contentBlockId: string | null): string {
    if (!contentBlockId) return '—';
    const block = this.contentBlocks().find(item => item.id === contentBlockId);
    return block ? block.title : contentBlockId;
  }

  private async startLiveUpdates(): Promise<void> {
    this.marketplaceLive = await subscribeMarketplaceOperationsLiveUpdates(
      this.currentContext.context()?.tenantId,
      this.liveUpdateService,
      () => this.load()
    );
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
